import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type { FailedCheckSnapshot } from "@/services/api/guardiansv2/types";

export interface TriggeringChecksPanelProps {
  checks: FailedCheckSnapshot[];
  catalogueKey?: string | null;
  onEditRule?: (catalogueKey: string, ruleId: string) => void;
  compact?: boolean;
}

/** Failed screening checks that raised or anchored an alert / audit. */
export function TriggeringChecksPanel({
  checks,
  catalogueKey,
  onEditRule,
  compact = false,
}: TriggeringChecksPanelProps) {
  if (checks.length === 0) return null;

  return (
    <section className="space-y-2" data-testid="triggering-checks-panel">
      {!compact ? (
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          What failed ({checks.length})
        </h3>
      ) : null}
      <div className="space-y-2">
        {checks.map((check, index) => {
          const failed = check.status !== "passed" && check.status !== "ignored";
          const canEdit = Boolean(catalogueKey && check.rule_id && onEditRule && check.kind !== "signal");
          const rowKey = check.rule_id ? `${check.kind}:${check.rule_id}:${index}` : `${check.kind}:${index}`;
          return (
            <div
              key={rowKey}
              className="flex items-start gap-3 rounded-lg border border-white/5 bg-slate-800/40 px-3 py-2"
              data-testid={`triggering-check-${check.rule_id}`}
            >
              <span
                className={clsx(
                  "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full",
                  failed ? "bg-rose-500/20 text-rose-300" : "bg-emerald-500/20 text-emerald-300"
                )}
                aria-hidden
              >
                <FontAwesomeIcon icon={failed ? faXmark : faCheck} className="h-2.5 w-2.5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="min-w-0 text-sm font-medium text-slate-200">{check.title}</p>
                  {canEdit ? (
                    <button
                      type="button"
                      onClick={() => onEditRule!(catalogueKey!, check.rule_id)}
                      className="shrink-0 text-xs text-slate-500 hover:text-slate-400"
                      data-testid={`edit-rule-${check.rule_id}`}
                    >
                      Edit rule →
                    </button>
                  ) : null}
                </div>
                {check.summary ? (
                  <p className="mt-0.5 text-sm leading-relaxed text-slate-100">{check.summary}</p>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
